/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ModalContext, Toaster } from '@notino/react-styleguide';

import useFeatureSwitches from '@hooks/useFeatureSwitches';
import {
  useAddToCartActions,
  useAddToCartButtonContext,
  useAddToCartSubmitAttempts,
} from '@sharedComponents/AddToCartButton/context';
import { IModal } from '@sharedComponents/AddToCartButton/context/model';
import { ProductWithCount } from '@sharedComponents/AddToCartButton/model';
import { IBuyButtonProps } from '@sharedComponents/AddToCartButton/sharedComponents/BuyButton';
import { useSystemOverloadedModal } from '@sharedComponents/AddToCartButton/SystemOverloadedModal';
import { wait } from '@utils/wait';

import { updateHeaderCartOverview } from '../utils';
import { addToCart } from '../utils/addToCart';
import { addToCartResult } from '../utils/addToCartResult';

type UseHandleAddToCartPropsType = Pick<IBuyButtonProps, 'isOnSpecialModal'>;

type IHandleAddToCart = {
  products?: ProductWithCount[];
};

export const useHandleAddToCart = ({
  isOnSpecialModal,
}: UseHandleAddToCartPropsType = {}) => {
  const {
    apollo: { addToCartMutation },
    props: {
      showAddToCartModal,
      product,
      onProductAdded,
      onProductAddFailed,
      count,
      services,
      onClosingModal,
    },
  } = useAddToCartButtonContext();
  const submitAttempts = useAddToCartSubmitAttempts();
  const { formatMessage } = useIntl();

  const {
    handleProductAddedToCart,
    handleIncreaseSubmitAttempts,
    handleFailedAddToCart,
    resetSendState,
  } = useAddToCartActions();

  const { addToCartWarningCount, addToCartResendDelay } = useFeatureSwitches();

  const { hideModal } = ModalContext.useModalContext();
  const showSystemOverloadedModal = useSystemOverloadedModal();

  const triggerElementRef = React.useRef<HTMLElement | null>(null);

  const returnFocusToTrigger = React.useCallback(() => {
    // The dialog's focus trap returns the focus on deactivation in a timeout of
    // its own (to the element focused when it activated - usually the body, as the
    // button is disabled while adding), so wait until it is done.
    setTimeout(() => setTimeout(() => triggerElementRef.current?.focus()));
  }, []);

  const handleAddToCart = React.useCallback(
    async ({ products: _products }: IHandleAddToCart) => {
      handleIncreaseSubmitAttempts();

      if (submitAttempts > 0 && addToCartResendDelay > 0) {
        await wait(addToCartResendDelay);
      }
      const products = _products || [{ ...product, count }];
      const addToCartResponse = await addToCart({
        addToCartMutation,
        products,
        services,
      });

      if (isOnSpecialModal) {
        hideModal();
      }

      if (!addToCartResponse.success) {
        handleFailedAddToCart();
        await Promise.resolve(
          onProductAddFailed(addToCartResponse.error, products)
        );
        if (
          submitAttempts >= addToCartWarningCount &&
          addToCartWarningCount > 0
        ) {
          showSystemOverloadedModal();
        }
        return;
      }

      const { response } = addToCartResponse;
      const noProductAddedCuzMaxedOut = response.productAddResponse?.every(
        (res) => res?.productIncrease === 0 && !!res.itemId
      );
      if (noProductAddedCuzMaxedOut) {
        const cart = response.productAddResponse?.[0]?.cart;
        const maxedProduct = response.productAddResponse?.find(
          (p) => p?.productIncrease === 0
        )!;
        Toaster.info({
          content: formatMessage(messages.maxProductCountReached, {
            maxCount:
              cart?.products?.find((p) => p?.itemId === maxedProduct?.itemId)
                ?.count ?? 0,
          }),
          icon: 'IconSolidInfo',
          closeIcon: true,
        });
        resetSendState();
        return;
      }

      const result = addToCartResult({
        response,
        products,
      });

      if (result.success) {
        await Promise.resolve(onProductAdded(products));
      } else {
        await Promise.resolve(
          onProductAddFailed(result.errorCode ?? '', products)
        );
      }

      updateHeaderCartOverview();

      let modal: IModal | null = null;
      if (result.notification) {
        modal = {
          type: result.notification.modalType,
        };
      } else if (showAddToCartModal) {
        modal = {
          type: 'upselling',
          onClosingModal,
          onClose: () => {
            onClosingModal();
            hideModal();
            returnFocusToTrigger();
          },
          returnFocus: returnFocusToTrigger,
        };
      }

      handleProductAddedToCart({
        result,
        products,
        modal,
        response,
        services,
      });
    },
    [
      handleIncreaseSubmitAttempts,
      submitAttempts,
      addToCartResendDelay,
      product,
      count,
      showAddToCartModal,
      addToCartMutation,
      services,
      isOnSpecialModal,
      hideModal,
      handleProductAddedToCart,
      resetSendState,
      onProductAdded,
      onProductAddFailed,
      onClosingModal,
      returnFocusToTrigger,
      formatMessage,
      handleFailedAddToCart,
      addToCartWarningCount,
      showSystemOverloadedModal,
    ]
  );

  const handleAddToCartEvent = React.useCallback(
    async (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      triggerElementRef.current = e.currentTarget;
      await handleAddToCart({ products: undefined });
    },
    [handleAddToCart]
  );

  return {
    handleAddToCartEvent,
    handleAddToCart,
  } as const;
};

const messages = defineMessages({
  maxProductCountReached: {
    id: 'universals.addToCart.maxProductCountReached',
    defaultMessage:
      'V košíku již máte maximalní počet daného produktu ({maxCount}). ',
  },
});
